import { useState } from 'react';

export default function ContentEditor({
  emailSubject,
  emailBody,
  whatsappMessage,
  channelMode,
  variables = [],
  onChange
}) {
  const [activeTab, setActiveTab] = useState(
    channelMode === 'WHATSAPP_ONLY' ? 'whatsapp' : 'email'
  );

  const insertVariable = (variable, field) => {
    const tag = `{{${variable.key}}}`;
    const currentValue = field === 'customEmailSubject' ? emailSubject
      : field === 'customEmailBody' ? emailBody
      : whatsappMessage;

    onChange(field, (currentValue || '') + tag);
  };

  const showEmail = channelMode !== 'WHATSAPP_ONLY';
  const showWhatsapp = channelMode !== 'EMAIL_ONLY';

  return (
    <div className="mb-6">
      {/* Tabs */}
      {showEmail && showWhatsapp && (
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'email'
                ? 'border-slate-400 text-slate-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📧 Email
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'whatsapp'
                ? 'border-slate-400 text-slate-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📱 WhatsApp
          </button>
        </div>
      )}

      {/* Variables Helper */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 mb-4">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
          Variables disponibles (cliquez pour insérer)
        </div>
        <div className="flex flex-wrap gap-2">
          {variables.map(v => (
            <button
              key={v.key}
              onClick={() => insertVariable(v, activeTab === 'email' ? 'customEmailBody' : 'customWhatsappMessage')}
              className="text-xs bg-white dark:bg-gray-700 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 hover:border-slate-300 transition-colors"
              title={v.example ? `Ex: ${v.example}` : v.label}
            >
              <code className="text-slate-700 dark:text-slate-500">{`{{${v.key}}}`}</code>
              <span className="text-gray-500 ml-1">{v.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Email Editor */}
      {showEmail && (activeTab === 'email' || !showWhatsapp) && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sujet de l'email
            </label>
            <input
              type="text"
              value={emailSubject || ''}
              onChange={e => onChange('customEmailSubject', e.target.value)}
              placeholder="Ex: Rappel: Paiement de {{amount_due}} en attente"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Corps de l'email
            </label>
            <textarea
              value={emailBody || ''}
              onChange={e => onChange('customEmailBody', e.target.value)}
              placeholder={`Bonjour {{customer_name}},

Nous vous rappelons qu'un paiement de {{amount_due}} est en attente depuis le {{due_date}} ({{days_overdue}} jours de retard).

Référence: {{reference}}

Merci de régulariser cette situation dans les meilleurs délais.

Cordialement,
L'équipe Fedis`}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Vous pouvez utiliser du HTML pour le formatage
            </p>
          </div>
        </div>
      )}

      {/* WhatsApp Editor */}
      {showWhatsapp && (activeTab === 'whatsapp' || !showEmail) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Message WhatsApp
          </label>
          <textarea
            value={whatsappMessage || ''}
            onChange={e => onChange('customWhatsappMessage', e.target.value)}
            placeholder={`Bonjour {{customer_name}} 👋

Rappel: un paiement de {{amount_due}} est en attente depuis {{days_overdue}} jours.

Réf: {{reference}}

Merci de régulariser. Pour toute question, répondez à ce message.`}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <div className="flex justify-between mt-1">
            <p className="text-xs text-gray-500">
              Max 4096 caractères. Les emojis sont autorisés.
            </p>
            <p className="text-xs text-gray-500">
              {(whatsappMessage || '').length} / 4096
            </p>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="mt-4">
        <details className="group">
          <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-slate-700">
            Aperçu avec valeurs d'exemple
          </summary>
          <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg space-y-4">
            {showEmail && emailSubject && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Sujet email</div>
                <div className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded border">
                  {renderPreview(emailSubject, variables)}
                </div>
              </div>
            )}
            {showEmail && emailBody && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Corps email</div>
                <div className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-3 rounded border whitespace-pre-wrap">
                  {renderPreview(emailBody, variables)}
                </div>
              </div>
            )}
            {showWhatsapp && whatsappMessage && (
              <div>
                <div className="text-xs text-gray-500 mb-1">WhatsApp</div>
                <div className="text-sm text-gray-900 dark:text-white bg-green-50 dark:bg-green-900/20 p-3 rounded-lg whitespace-pre-wrap">
                  {renderPreview(whatsappMessage, variables)}
                </div>
              </div>
            )}
          </div>
        </details>
      </div>
    </div>
  );
}

function renderPreview(template, variables) {
  if (!template) return '';

  let result = template;
  for (const v of variables) {
    const regex = new RegExp(`\\{\\{${v.key}\\}\\}`, 'g');
    result = result.replace(regex, v.example || `[${v.label}]`);
  }
  return result;
}
